import React, { useState } from 'react';
import classNames from 'classnames';
import { useDispatch } from 'react-redux';
import Popover, { PopoverStatus } from '../general-components/popover';
import { BroadcastOption, broadcastOptions } from './broadcast-controls';
import * as BroadcastActions from '../../stores/actions/broadcasting';
import useRoomState from '../../hooks/use-room-state';
import Socket from '../../connection/socket';
import { ROOM_STATES } from '../../stores/reducers/settings';

interface BroadcastDropdownProps {
	socket: Socket | null;
	roomState: number;
	selectedBroadcastOption: BroadcastOption;
	isOpen: boolean;
	onClose: () => void;
	setPopoverStatus?: React.Dispatch<React.SetStateAction<PopoverStatus>>;
}

export default function BroadcastDropdown(props: BroadcastDropdownProps) {
	const {
		socket,
		roomState,
		selectedBroadcastOption,
		isOpen,
		onClose,
		setPopoverStatus,
	} = props;

	const dispatch = useDispatch();
	const { cycleRoomState } = useRoomState(socket);

	const [hoverOption, setHoverOption] = useState<string>('');

	function handleBroadcastOptionChange(option: BroadcastOption) {
		if (isOpen) {
			onClose();
		}

		switch (option.value) {
			case 'stream': {
				if (roomState === ROOM_STATES.EDITING) {
					cycleRoomState();
				}
				break;
			}
			case 'record': {
				if (roomState === ROOM_STATES.EDITING) {
					cycleRoomState();
				}
				break;
			}
			case 'showbuilder': {
				if (roomState !== ROOM_STATES.EDITING) {
					cycleRoomState();
				}
				break;
			}
			default: {
				console.log(`Unknown broadcast option ${option.value}`);
			}
		}

		if (typeof option.number === 'number') {
			dispatch(BroadcastActions.setRecordingOption(option.number));
		}
	}

	return (
		<Popover
			open={isOpen}
			close={onClose}
			className='broadcast-dropdown-wrapper'
			waitForAnimation={true}
			setPopoverStatus={setPopoverStatus}
		>
			<div
				className={classNames('broadcast-dropdown')}
				data-testid='broadcast-dropdown'
			>
				{broadcastOptions.map(({
					value,
					dropdownIcon: DropdownIcon,
					dropdownTitle,
					dropdownSubtitle,
				}) => {
					const isSelected = selectedBroadcastOption.value === value;
					const isHovered = hoverOption === value;
					return (
					<div
						key={value}
						className={
							classNames(
								'broadcast-dropdown-option',
								(isSelected || isHovered) && 'selected',
								value,
							)
						}
						onClick={(e) => {
							e.stopPropagation();
							handleBroadcastOptionChange(
								broadcastOptions.find(
									(_option) => _option.value === value,
								) as BroadcastOption,
							);
						}}
						onMouseOver={() => setHoverOption(value)}
						onMouseOut={() => setHoverOption('')}
						style={{ opacity: (isSelected && hoverOption && !isHovered) ? 0.6 : 1 }}
						data-testid='dropdown-option'
					>
						<div className="dropdown-image-wrapper">
							{ DropdownIcon && <DropdownIcon /> }
						</div>
						<div className="flex flex-flow-column white-space-nowrap">
							<span className="title">{dropdownTitle}</span>
							<span className="sub-title">{dropdownSubtitle}</span>
						</div>
					</div>
					);
				}).reverse()}
			</div>
		</Popover>
	);
}
